import {
  Component,
  ViewChild,
  TemplateRef,
  inject,
  OnDestroy,
} from '@angular/core';
import { SearchLocationModalComponent } from '../search-location-modal/search-location-modal.component';
import { NgbModal, NgbModalOptions } from '@ng-bootstrap/ng-bootstrap';
import { User } from 'src/app/models/user';
import { FormGroup, FormControl, Validators } from '@angular/forms';
import { UserService } from 'src/app/services/user.service';
import { LoaderService } from 'src/app/services/loader.service';
import { Subscription, catchError, finalize } from 'rxjs';
import { ToastrService } from 'ngx-toastr';
import { RegistrationDoneModalComponent } from '../registration-done-modal/registration-done-modal.component';
import { CustomValidators } from 'src/app/shared/validators/custom.validator';
import { LocationSelection, locationCity, validLocation } from 'src/app/services/location-lookup.service';
import { ErrorHandlerService } from 'src/app/shared/error-handler/error-handler.service';

@Component({
  selector: 'app-profile',
  templateUrl: './profile-modal.component.html',
  styleUrls: ['./profile-modal.component.scss'],
})
export class ProfileModalComponent implements OnDestroy {
  // #region INPUT, OUTPUT, VIEWCHILD
  @ViewChild('profileModal')
  profileTemplate!: TemplateRef<ProfileModalComponent>;
  @ViewChild('searchLocationModal')
  searchLocationComponent!: SearchLocationModalComponent;
  @ViewChild('registrationDoneModal')
  registrationDoneComponent!: RegistrationDoneModalComponent;
  // #endregion

  // Profile Tab Switcher
  selectedTab = 1;
  file: File;
  user: User = new User();
  private subscription: Subscription;
  locations: {
    address: string;
    latitude: number;
    longitude: number;
    addressComponent?: google.maps.GeocoderAddressComponent[];
    name?: string;
  }[];

  private modalService = inject(NgbModal);
  constructor(
    private userService: UserService,
    private loaderService: LoaderService,
    private toastr: ToastrService
  ) {
    this.user = this.userService.getUser();
    this.userService.userData$.subscribe((userData) => {
      this.user = userData;
    });
  }

  FillAddressDetails(location: LocationSelection) {
    if (!validLocation(location)) return;
    this.locations = [location];

    this.formGroup.patchValue({
      Address: location.address,
      City: locationCity(location) || this.formGroup.controls['City'].value || '',
      Zip: location.addressComponent?.find(item => item.types.includes('postal_code'))?.long_name
        || this.formGroup.controls['Zip'].value || '',
    });
    this.userService.setProfileSelectedLocation(location.address, location.latitude, location.longitude);
  }

  formGroup = new FormGroup({
    Address: new FormControl('', Validators.maxLength(255)),
    City: new FormControl('', Validators.maxLength(30)),
    Zip: new FormControl('', [
      Validators.minLength(5),
      Validators.maxLength(15),
      CustomValidators.zipCodeValidator(),
    ]),
    Phone: new FormControl('', [
      Validators.minLength(7),
      Validators.maxLength(15),
      CustomValidators.PhoneNumberValidator(),
    ]),
    Instagram: new FormControl('', [Validators.maxLength(30)]),
    Tiktok: new FormControl('', [CustomValidators.tikTokValidator(), Validators.maxLength(24)]),
    Facebook: new FormControl('', [CustomValidators.facebookValidator(), Validators.minLength(5), Validators.maxLength(15)]),
    ProfilePicture: new FormControl(''),
  });

  selectTab(tabNumber: number): void {
    this.selectedTab = tabNumber;
  }
  nextTab(): void {
    this.selectedTab = (this.selectedTab % 2) + 1;
  }

  openModal(modal: TemplateRef<any>, windowClass: string = '') {
    this.modalService.open(modal, {
      windowClass: `project-modal ${windowClass}`,
      centered: true,
      backdropClass: 'primary-backdrop',
    });
  }

  onFileSelected(event: any) {
    this.file = event.target.files[0];
    if (!this.file.type.startsWith('image/')) {
      this.toastr.error('Please select image.');
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      this.user.profilePicture = reader.result as string;
      localStorage.setItem('ProfileUrl', this.user.profilePicture);
    };
    reader.readAsDataURL(this.file);
  }

  onNextClick() {
    // create user with signup details & personal details
    this.user.address = this.formGroup.get('Address').value;
    this.user.city = this.formGroup.get('City').value;
    this.user.zip = this.formGroup.get('Zip').value;
    this.user.mobileNumber = parseFloat(this.formGroup.get('Phone').value);
    this.user.instagramId = this.formGroup.get('Instagram').value;
    this.user.tikTokId = this.formGroup.get('Tiktok').value;
    this.user.faceBookId = this.formGroup.get('Facebook').value;
    this.user.profilePicture = this.file;
    this.user.Lat = this.locations && this.locations.length > 0 && this.locations[0].latitude !== undefined ? this.locations[0].latitude : 0.00;
    this.user.Lng = this.locations && this.locations.length > 0 && this.locations[0].longitude != undefined ? this.locations[0].longitude : 0.00;
    this.userService.updateUser(this.user);
    this.saveDetails(this.user);
    this.resetForm();
  }

  resetForm() {
    this.formGroup.reset();
    this.formGroup.updateValueAndValidity();
    this.user = new User();
  }

  saveDetails(user: User) {
    this.loaderService.setLoading(true);
    this.subscription = this.userService
      .registerWithProfilePicture(user)
      .pipe(finalize(() => this.loaderService.setLoading(false)))
      .subscribe({
        next: (response) => {
          this.toastr.success('User created successfully!');
          if (response.picture === 'deferred') {
            this.toastr.warning('Your account was created, but your picture was not saved. You can add it after signing in.');
          }
          this.openModal(
            this.registrationDoneComponent.registrationDoneTemplate,
            'registration-done-modal'
          );
          this.userService.completeRegistration(user);
        },
        error: error => {
          this.toastr.error(ErrorHandlerService.toMessage(error));
        }
      });
  }

  skipBtnClicked() {
    // create user without personal details
    this.loaderService.setLoading(true);
    this.subscription = this.userService
      .create('/api/tenants/membershipApp/CreateMembershipUser', this.user)
      .pipe(
        catchError((error) => {
          this.loaderService.setLoading(false);
          throw error;
        })
      )
      .subscribe((response) => {
        this.toastr.success('User created successfully!');
        this.openModal(
          this.registrationDoneComponent.registrationDoneTemplate,
          'registration-done-modal'
        );
        this.loaderService.setLoading(false);
        this.userService.completeRegistration(this.user);
      });
  }

  ngOnDestroy() {
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
  }
}
